// @skip-page-container-check
// Full-screen 404 — split-dark language (see docs/STYLING_SYSTEM.md §7).
// Glass modal, shader bg.
import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRight, Brackets } from "lucide-react"
import { LusterShaderSlot } from "../components/LusterShaderSlot"
import { ActionSidebar } from "../components/ActionSidebar"
import { ToolGrid } from "../components/ToolGrid"
import { FreakoutWord } from "../lib/freakoutWord"
import { useBodyScrollLock } from "../lib/useBodyScrollLock"
import { useGlitchEnabled } from "../lib/useGlitchEnabled"

// Color bias overrides for the 404 — matches the narrative (fragmentation vocabulary)
const FAULT_COLOR_BIAS: Record<string, string> = {
  ERR:          'text-red-400',
  false:        'text-red-400',
  undefined:    'text-red-400',
  Nothing:      'text-red-400',
  router:       'text-luster-400',
  '404':        'text-luster-400',
  platform:     'text-teal-400',
  Spreadsheets: 'text-teal-400',
  Scattered:    'text-teal-400',
  failures:     'text-red-400',
  source:       'text-luster-400',
  failed:       'text-red-300',
  score:        'text-luster-400',
  supply:       'text-amber-400',
  health:       'text-teal-400',
  task:         'text-violet-400',
  system:       'text-teal-400',
  TRACE:        'text-luster-400',
  GET:          'text-teal-400',
}

const FAULT_HOT_WORDS = new Set([
  'ERR', 'false', 'undefined', 'router', '404', 'platform',
  'Nothing', 'Spreadsheets', 'Scattered', 'failures', 'source', 'failed',
])

export default function NotFoundPage() {
  useBodyScrollLock()
  const navigate = useNavigate()
  const [glitchOn] = useGlitchEnabled()
  const [burst, setBurst] = useState(false)
  const burstTimer = useRef<ReturnType<typeof setTimeout>>()
  const burstRafRef = useRef<number>(0)

  const triggerBurst = () => {
    clearTimeout(burstTimer.current)
    cancelAnimationFrame(burstRafRef.current)
    setBurst(false)
    burstRafRef.current = requestAnimationFrame(() => {
      setBurst(true)
      burstTimer.current = setTimeout(() => setBurst(false), 1600)
    })
  }

  const stopBurst = () => {
    cancelAnimationFrame(burstRafRef.current)
    clearTimeout(burstTimer.current)
    setBurst(false)
  }

  useEffect(() => () => { cancelAnimationFrame(burstRafRef.current); clearTimeout(burstTimer.current) }, [])

  // Shared color bias + hot words for all GlitchWords on this page
  const wordProps = {
    hotWords:   FAULT_HOT_WORDS,
    colorBias:  FAULT_COLOR_BIAS,
    intensity:  3.25,
  }

  return (
    <div className="fixed inset-0 z-[70] bg-luster-950">

      {/* Full-screen shader */}
      <div className="absolute inset-0 z-0 opacity-90">
        <LusterShaderSlot fill scale={1.2} fps={30} />
      </div>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.15)_100%)]" />

      {/* Centered dark glass modal — same size as Apply/Login; scrollable when content overflows */}
      <div className="relative z-10 flex items-center justify-center min-h-full px-4 sm:px-10 lg:px-16 py-4 sm:py-10 lg:py-12">
        <div className="relative w-full max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-6rem)] rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] flex flex-col animate-in fade-in zoom-in-95 duration-500">

          {/* Header row — page code + title + ActionSidebar */}
          <div className="shrink-0 flex items-center justify-between gap-4 px-8 sm:px-12 lg:px-14 pt-8 sm:pt-10 lg:pt-10 pb-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <img src="/brand/macOS/app-icon-256.png" alt="Luster" className="w-10 h-10 rounded-xl object-cover shrink-0" />
              <div className="space-y-0.5">
                <p className="font-mono text-[10px] text-white/20 flex items-center gap-1.5">
                  <span>0xFF</span>
                  <span className="text-white/10">·</span>
                  <span className="text-red-400/40">fault.resolve()</span>
                </p>
                <span className="font-mono text-[10px] text-white/30 tracking-[0.2em] uppercase">Route not resolved</span>
              </div>
            </div>
            <ActionSidebar />
          </div>

          <div className="overflow-y-auto overscroll-contain min-h-0 px-8 sm:px-12 lg:px-14 pt-6 sm:pt-8 lg:pt-8 pb-6 sm:pb-8 lg:pb-8 space-y-6">

            {/* Title + terminal */}
            <div className="space-y-4 md:space-y-5">
              <h1 className="text-white tracking-tight leading-tight font-mono flex items-baseline gap-1 flex-wrap">
                <span className="text-red-400/90 text-2xl md:text-3xl">
                  <FreakoutWord word="ERR" {...wordProps} colorBase="red" burst={burst} disabled={!glitchOn} />
                </span>
                <span className="text-white/90 text-4xl md:text-5xl">404</span>
                <span className="text-white/60 text-lg md:text-xl">NOT_FOUND</span>
              </h1>

              <div className="font-mono text-[11px] space-y-1.5">
                <p><span className="text-white/25">0xDEAD</span>{"  "}<span className="text-luster-400"><FreakoutWord word="TRACE" {...wordProps} colorBase="luster" burst={burst} disabled={!glitchOn} /></span>{"  "}<span className="text-white/40">router.resolve()</span></p>
                <p><span className="text-luster-400"><FreakoutWord word="GET" {...wordProps} colorBase="teal" burst={burst} disabled={!glitchOn} /></span>{"    "}<span className="text-white/50">{window.location.pathname}</span></p>
                <p><span className="text-red-400">←</span>{"      "}<span className="text-white/40">404 NOT_FOUND</span></p>
                <p><span className="text-white/20">↳</span>{"      "}<span className="text-white/30">segment match: </span><span className="text-red-300/80"><FreakoutWord word="false" {...wordProps} colorBase="red" burst={burst} disabled={!glitchOn} /></span></p>
                <p><span className="text-red-400"><FreakoutWord word="ERR" {...wordProps} colorBase="red" burst={burst} disabled={!glitchOn} /></span>{"    "}<span className="text-white/40">dispatch() — unhandled exception</span></p>
                <p><span className="text-white/20">↳</span>{"      "}<span className="text-white/25">fallback: </span><span className="text-red-300/60"><FreakoutWord word="undefined" {...wordProps} colorBase="red" burst={burst} disabled={!glitchOn} /></span></p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/[0.06]" />

            {/* Copy — one paragraph structure, tight lines, semantic h2–h4 */}
            <div className="space-y-1">
              <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-tight">
                <FreakoutWord word="Nothing" bold burst={burst} disabled={!glitchOn} /> here. Everything elsewhere.
              </h2>
              <h3 className="text-[15px] font-normal tracking-wide leading-tight">
                <span className="text-white/20" aria-hidden>↳</span>{' '}
                <span className="text-white/25">outcome:</span>{' '}
                <span className="text-red-300/60">The route </span><span className="text-red-400"><FreakoutWord word="failed" burst={burst} disabled={!glitchOn} /></span><span className="text-red-300/60">.</span>
              </h3>
              <h3 className="text-[15px] font-medium text-white/60 leading-tight">
                {/* Easter egg — hover/touch triggers freakout burst */}
                <span
                  onMouseEnter={triggerBurst}
                  onMouseLeave={stopBurst}
                  onTouchStart={(e) => { e.preventDefault(); triggerBurst() }}
                  onTouchEnd={stopBurst}
                  onTouchCancel={stopBurst}
                  className="cursor-crosshair text-teal-400 touch-manipulation"
                >
                  <FreakoutWord word="Spreadsheets" bold burst={burst} disabled={!glitchOn} />
                </span>{' '}do too.
              </h3>
              <h4 className="text-[15px] font-normal leading-snug text-white/50 pt-0.5">
                <FreakoutWord word="Scattered" burst={burst} disabled={!glitchOn} /> data, silent{' '}
                <FreakoutWord word="failures" burst={burst} disabled={!glitchOn} />, no single{' '}
                <FreakoutWord word="source" burst={burst} disabled={!glitchOn} /> of truth.
              </h4>
              <ToolGrid externalBurst={burst} className="mt-4 md:mt-3" />
            </div>

            {/* CTAs — single primary action; Home/Back/Glitch live in ActionSidebar */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/apply")}
                className="w-full h-14 rounded-2xl bg-amber-400/10 border border-amber-400/20 text-amber-200 hover:bg-teal-400/10 hover:border-teal-400/20 hover:text-teal-200 shadow-lg shadow-black/20 flex items-center justify-center gap-2.5 transition-colors duration-700 active:scale-[0.98] group"
              >
                <span className="uppercase tracking-[0.15em] text-[11px] font-black">Request Access</span>
                <ArrowRight className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200" />
              </button>
            </div>

            {/* Minimal footer */}
            <div className="border-t border-white/[0.04] pt-4 flex items-center justify-between">
              <p className="font-mono text-[9px] text-white/15">
                <span className="text-amber-400/30">0xFF</span>
                <span className="text-white/[0.08]"> · </span>
                <span className="text-white/10">fault.resolve()</span>
              </p>
              <p className="font-mono text-[9px] text-white/10">luster.company</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
